import { useMemo, useEffect, useRef, useCallback } from "react";
import { isFunction } from "utils/isFunction";
import { EditorResizeObserver } from "./observer";
import { transform, MentionExtension } from "./mention";
import Editor from "./EditorClass";

export function useEditor({
	reference,
	content,
	focus,
	disabled,
	bindings,
	context,
	// TODO: maxTextLength, after serialization.
	// maxTextLength,
	handleResized,
	onFocus,
	onBlur,
	autoPositioning,
}) {
	const internal = {
		core: useRef(null),
		container: useRef(null),
		contenteditable: useRef(null),
		spacing: useRef(null),
		mentions: useRef(null),
		selection: useRef(null),
	};

	const external = {
		editor: useRef(null),
	};

	const editor = reference || external.editor;

	editor.current = {
		focus(cursorToEnd = false) {
			if (disabled) {
				return false;
			}

			internal.contenteditable.current.focus();

			if (cursorToEnd) {
				const selection = internal.core.current.getSelection();

				const line =
					internal.contenteditable.current.childNodes[
						internal.contenteditable.current.childNodes.length - 1
					];

				const range = document.createRange();

				range.setEndAfter(line.childNodes[line.childNodes.length - 1]);
				range.setStartAfter(
					line.childNodes[line.childNodes.length - 1]
				);
				if (selection) {
					selection.setRange(range);
				}
			}
		},

		blur() {
			internal.contenteditable.current.blur();
		},

		addHotkey(handler) {
			internal.core.current.hotkeys.push(handler);
		},

		removeHotkey(handler) {
			const index = internal.core.current.hotkeys.indexOf(handler);

			if (index >= 0) {
				internal.core.current.hotkeys.splice(index, 1);
			}
		},

		serialize() {
			const serialized = internal.core.current.content.serialize();

			const plainMention = serialized.reduce((result, node) => {
				if (typeof node === "string") {
					return `${result}${node}`;
				}

				// NOTE: This should be always an array (for now).
				if (Array.isArray(node)) {
					const reduced = node.reduce((result, node) => {
						if (typeof node === "string") {
							return result + node;
						}
						return result + node.serializeMentionFull();
					}, "");

					return `${result}${reduced}\n`;
				}

				return `${result}${node ? node.serialize() : null}\n`;
			}, "");

			const plain = serialized.reduce((result, node) => {
				if (typeof node === "string") {
					return `${result}${node}`;
				}

				// NOTE: This should be always an array (for now).
				if (Array.isArray(node)) {
					const reduced = node.reduce((result, node) => {
						if (typeof node === "string") {
							return result + node;
						}
						return result + node.serialize();
					}, "");

					return `${result}${reduced}\n`;
				}

				return `${result}${node ? node.serialize() : null}\n`;
			}, "");
			const parsed = [];

			serialized.forEach((line) => {
				if (typeof line === "string") {
					if (typeof parsed[parsed.length - 1] === "string") {
						parsed[parsed.length - 1] += `${line}\n`;
						return;
					}

					parsed.push(`${line}\n`);
					return;
				}

				line.forEach((node) => {
					if (typeof node === "string") {
						if (typeof parsed[parsed.length - 1] === "string") {
							parsed[parsed.length - 1] += node;
							return;
						}

						parsed.push(node);
						return;
					}

					parsed.push(node.data);
				});

				if (typeof parsed[parsed.length - 1] === "string") {
					parsed[parsed.length - 1] += "\n";
					return;
				}

				parsed.push("\n");
			});

			return {
				plain: plain.replace(/[\u00A0]+/g, " "),
				plainMention: plainMention.replace(/[\u00A0]+/g, " "),
				parsed,
			};
		},

		deserialize(content) {
			if (content == null) {
				return [];
			}

			let { plain, parsed } = content;

			if (plain == null) {
				plain = "";
			}

			if (!Array.isArray(parsed)) {
				parsed = [];
			}

			const result = plain.split(/\r\n|\r|\n/);

			const entities = parsed.filter(
				(entity) =>
					entity && entity.type != null && entity.type === "user"
			);

			let entityIndex = 0;

			// TODO: Use reduce for better readibility.

			let previous = null;
			for (let index = 0; index < result.length; index++) {
				const line = result[index];

				if (
					!line.trim().length &&
					typeof previous === "string" &&
					!previous.trim().length
				) {
					result.splice(index, 1);
					previous = line;
					// eslint-disable-next-line no-continue
					continue;
				}

				// TODO: Match for mentions.
				// eslint-disable-next-line no-loop-func
				result[index] = transform(line, (match) => {
					const entity = entities[entityIndex++];

					// if (entity === undefined) {
					// 	return "match[0]";
					// }

					if (entity == null) {
						return match[0];
					}

					return {
						type: "mention",
						data: {
							type: match[1],
							id: match[2],
							name: entity?.name?.substring(1),
						},
					};
				});

				previous = line;
			}

			return result;
		},

		replaceWith(text) {
			internal.core.current.replaceWithText(text);
		},

		clear() {
			internal.core.current.clear();
		},
	};

	const observer = useMemo(() => new EditorResizeObserver(), []);
	const resized = useMemo(() => ({}), []);

	const onResize = useCallback((name, target) => {
		const box = target.getBoundingClientRect();

		resized[name] = {
			width: target.offsetWidth,
			height: box.height,
		};

		if (isFunction(handleResized)) {
			handleResized(resized);
		}
	});

	const onContentResize = () => {
		const result = {
			width: 0,
			height: 0,
		};

		if (
			internal.contenteditable.current &&
			internal.contenteditable.current.childNodes
		) {
			if (
				internal.contenteditable.current.childNodes[0]?.nodeName == "BR"
			) {
				const line = internal.core.current.content.dom.create({
					element: "p",
					className: "editor-paragraf",
					children: { element: "br" },
				});
				internal.contenteditable.current.childNodes[0].remove();
				internal.contenteditable.current.appendChild(line);
				internal.core.current.selection.setCursor(line.lastChild);
			}

			for (const node of internal.contenteditable.current.childNodes) {
				if (node instanceof Element) {
					const box = node.getBoundingClientRect();

					// TODO: Get padding x for container.

					result.width = Math.max(result.width, box.width + 22);
					result.height = Math.max(result.height, box.height);
				}
			}
		}
		resized.container = result;

		if (isFunction(handleResized)) {
			handleResized(resized);
		}
	};

	const autoPositioningRef = useRef(autoPositioning);
	useEffect(() => {
		autoPositioningRef.current = autoPositioning;
	}, [autoPositioning]);

	useEffect(() => {
		const core = new Editor({
			attach: internal.contenteditable.current,
			value: editor.current.deserialize(content),
			context,
			extensions: [MentionExtension],
			bindings,
		});

		internal.contenteditable.current.addEventListener("focus", (event) => {
			onFocus(event);
		});

		internal.contenteditable.current.addEventListener("blur", (event) =>
			onBlur(event)
		);

		internal.core.current = core;

		observer.observe(internal.container, (state) => {
			onResize("root", state.target);
			onContentResize();
		});

		internal.contenteditable.current.addEventListener(
			"keydown",
			onContentResize
		);

		internal.contenteditable.current.addEventListener(
			"paste",
			onContentResize
		);

		internal.contenteditable.current.addEventListener(
			"cut",
			onContentResize
		);

		// MARCIN_P: Dwa listenery do otwierania i zamykania mention.
		internal.contenteditable.current.addEventListener("input", (e) => {
			if (e.data === "@" && !core.isActive) {
				core.isActive = true;
			} else if (e.data !== "@" && core.isActive) {
				core.isActive = false;
			}
		});
		internal.contenteditable.current.addEventListener("click", () => {
			if (core.isActive) {
				core.isActive = false;
			}
		});
		////////////////////////////////////////////////////

		observer.observe(internal.spacing, (state) =>
			onResize("spacing", state.target)
		);

		onContentResize();

		if (focus) {
			editor.current.focus(true);
		}

		if (disabled) {
			internal.contenteditable.current.setAttribute(
				"contenteditable",
				"false"
			);
		}

		return () => {
			if (core.controller) {
				core.controller.abort();
			}

			observer.clear();
			core.detach();
		};
	}, []);

	const isScrollingRef = useRef(false);
	useEffect(() => {
		onContentResize(); /* MARCIN_P (DZ-1891): Fn "onContentResize" in useEffect fixes refreshing the width of the textbox after adding only emojis. */

		if (autoPositioning) {
			const containerRect =
				internal.container.current.getBoundingClientRect();
			const isBottomEdgeHidden =
				containerRect.bottom - 1 > window.innerHeight;
			if (isBottomEdgeHidden && !isScrollingRef.current) {
				isScrollingRef.current = true;
				internal.container.current.scrollIntoView({
					behavior: "smooth",
					block: "end",
				});
				setTimeout(() => {
					isScrollingRef.current = false;
				}, 500);
			}
		}
	}, [
		internal.contenteditable?.current?.innerText.trim().length,
		isScrollingRef.current,
	]);

	if (internal.core.current) {
		internal.core.current.context = context;
	}

	return {
		current: editor.current,
		internal,
	};
}
